import {HStack} from "@chakra-ui3/react";
import {Owner, OwnerType, TeamMin} from "../../Types.ts";
import {useCallback, useState} from "react";
import {TeamPicker, TeamPickerValue} from "../../components/TeamPicker.tsx";
import api from "../../api/index.ts";
import {usePromiseState} from "../../hooks/promiseState.ts";
import {InnerDialogProps, withDialog} from "../../state/withDialog.tsx";
import {DialogBody, DialogFooter, DialogHeader, DialogTitle} from "../../components/ui/dialog.tsx";
import {Button} from "../../components/ui/button.tsx";
import {useModalContext} from "../../components/modals";
import {createEditTeamDialog} from ".";

const ReassignTeamDialog = withDialog(
  ({
    onClose,
    onReassign,
  }: InnerDialogProps & {
    onReassign: (owner?: Owner) => Promise<unknown>;
  }) => {
    const {open} = useModalContext();
    const [teamOrCreate, setTeamOrCreate] = useState<TeamPickerValue | null>(null);

    const reassignTeam = useCallback(
      async (team: TeamMin) => {
        const owner = await api.owners.resolve([{type: OwnerType.Team, content: team.team_id}]);
        await onReassign(owner[0]);
      },
      [onReassign],
    );

    const [reassigning, reassign] = usePromiseState(async () => {
      if (teamOrCreate?.type === "Team") {
        const owner = await api.owners.resolve([{type: OwnerType.Team, content: teamOrCreate.team.team_id}]);
        await onReassign(owner[0]);
      } else if (teamOrCreate?.type === "Create") {
        // Autofocus doesn't work properly unless we add a delay between closing and re-opening a dialog
        setTimeout(
          () => open(createEditTeamDialog, {type: "Create", onCreate: reassignTeam, initialName: teamOrCreate.name}),
          200,
        );
      }
      onClose();
    }, [onReassign, onClose, teamOrCreate, open, reassignTeam]);

    return (
      <>
        <DialogHeader>
          <DialogTitle>Reassign to</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <TeamPicker value={teamOrCreate} onSelect={setTeamOrCreate} onSubmit={reassign} autoFocus />
        </DialogBody>
        <DialogFooter>
          <HStack gap="3">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorPalette="blue"
              onClick={reassign}
              disabled={!teamOrCreate || reassigning.inProgress}
              loading={reassigning.inProgress}
            >
              {teamOrCreate?.type === "Create" ? "Create and reassign" : "Reassign"}
            </Button>
          </HStack>
        </DialogFooter>
      </>
    );
  },
);

export default ReassignTeamDialog;
